import type { ResultCategory } from '@/domain/types';

/**
 * ===========================================================================
 * RESULT COPY — NEEDS JAMES'S REVIEW
 * ===========================================================================
 *
 * Three categories, three tones. The rules that shaped them:
 *
 *  - Nothing is framed as a verdict. A result is an opening line, not a score.
 *  - The "different paths" copy must be genuinely kind. She gave you three
 *    minutes of her attention; the last thing she reads should be warm, and it
 *    should not sound like a rejection letter or like a door slamming.
 *  - No numbers. No percentages. No "match strength". The model does not
 *    support that precision and pretending otherwise would be dishonest.
 */

export interface ResultCopy {
  /** Small label above the headline. */
  eyebrow: string;
  headline: string;
  /** One or two sentences directly under the headline. */
  lede: string;
  /** Heading above the alignment list. */
  alignmentHeading: string;
  /**
   * Heading above the near-agreements on the questions that decide things.
   *
   * Its own list, not a softer word for "differ". These are answers that point
   * the same way without being the same answer, and the only honest thing to
   * do with them is name them and hand them back as a conversation.
   */
  conversationHeading: string;
  /** Heading above the real differences. */
  differenceHeading: string;
  /** Heading above the overlap found in the optional round. */
  everydayHeading: string;
  /** The honest caveat. Shown on every result, every time. */
  caveat: string;
  /** Copy for the next-step block. */
  nextStep: {
    heading: string;
    body: string;
    /** Label on the button that opens the contact form. Null = no form offered. */
    cta: string | null;
  };
  /** Shown when the visitor declines to get in touch. */
  decline: string;
}

export const resultCopy: Record<ResultCategory, ResultCopy> = {
  'strong-potential': {
    eyebrow: 'Where we landed',
    headline: 'There may be something worth exploring here.',
    lede: "A quiz can't measure chemistry, but it looks as though we share some of the foundations, and those are the parts that are hard to negotiate later.",
    alignmentHeading: 'Where we align',
    conversationHeading: 'Worth talking about',
    differenceHeading: 'Where we differ',
    everydayHeading: 'Everyday overlap',
    caveat:
      "This is a handful of questions, not a prediction. Plenty of good relationships would score badly here, and plenty of compatible people never quite click. It's a starting point, nothing more.",
    nextStep: {
      heading: 'Want to find out the rest in person?',
      body: "If you'd like to, leave me one way to reach you. A coffee or a walk, no expectations either way.",
      cta: 'Say hello',
    },
    decline: "No pressure at all. Thank you for giving it a go.",
  },

  'worth-a-conversation': {
    eyebrow: 'Where we landed',
    headline: "Some real overlap, and a few honest differences.",
    lede: "We're not identical, which is not a problem. The interesting part is usually in the gap, and there's enough common ground here to be worth an actual conversation.",
    alignmentHeading: 'Where we align',
    conversationHeading: 'Worth talking about',
    differenceHeading: 'Where we differ',
    everydayHeading: 'Everyday overlap',
    caveat:
      "A set of questions can't tell you much about how two people actually get on. Differences on a page and differences in person are very different things.",
    nextStep: {
      heading: 'Curious enough for a coffee?',
      body: "If you'd like to talk properly, leave me one way to reach you. If not, that's completely fine.",
      cta: 'Say hello',
    },
    decline: "Completely fine. Thank you for taking the time.",
  },

  'different-paths': {
    eyebrow: 'Where we landed',
    headline: 'It looks as though we may be after different things.',
    lede: "Thank you for giving this a go. Properly, I mean. I'd rather find this out in three honest minutes than three months in, and I suspect you would too.",
    alignmentHeading: 'We did agree on',
    conversationHeading: 'Worth talking about',
    differenceHeading: 'Where we differ',
    everydayHeading: 'Everyday overlap',
    caveat:
      "None of this says anything about you. It only says our answers point in different directions, and that is a completely ordinary thing for two people to discover.",
    nextStep: {
      heading: 'Genuinely, all the best',
      body: "I hope you find exactly what you're looking for in your own love story. If you know someone this might suit better, the card is yours to pass on.",
      cta: null,
    },
    decline: '',
  },
};

/**
 * The contents of the "Exactly what gets sent" disclosure on the contact form.
 * REVIEW: this is a promise. Make sure you can keep every line of it.
 *
 * The panel is collapsed by default now. That was a presentation decision, not
 * a retreat: it opens on one tap, it still lists every field, and it still says
 * so before anything is sent. What changed is that it no longer greets someone
 * who has only just decided to say hello.
 */
export const contactPrivacyCopy = {
  heading: 'Exactly what gets sent',
  items: [
    'Your first name',
    'The one contact detail you choose to give me',
    'Your message, if you write one',
  ],
  /**
   * Listed only when there is a result to send.
   *
   * The form is now reachable from the profile without answering anything, and
   * on that route there is no result: the payload carries `null` and the
   * notification says she came straight from the page. A panel headed "exactly
   * what gets sent" may not name a field that is not being sent.
   */
  resultItem: 'Which of the three results you got',
  /** Shown instead, on the direct route, so the absence is stated rather than implied. */
  noResultItem: 'No compatibility result. You have not done the questions',
  optionalItem: 'The answers you gave. Only if you tick the box',
  shareAnswersLabel: 'Also send my answers, so he has some context',
  assurance:
    'Nothing is sent until you press the button. No account, no mailing list, no tracking pixel, and nothing shared with anyone else.',
  /**
   * There is no deletion job, and the notification email lands in a personal
   * inbox, so any fixed period would be a promise nothing enforces. This says
   * only what is actually true and actually in James's gift to honour.
   */
  retention: 'I keep what you send until you ask me to delete it. Ask any time.',
};
